import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Ban, Database, Hammer, Layers, Loader2 } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { ConversationModeSwitchDialog } from '../../components/ConversationModeSwitchDialog';
import { ThreadUsageCard } from '../../components/ThreadUsageCard';
import { ThreadUsageModal } from '../../components/ThreadUsageModal';
import { useOpenConversation } from '../../hooks/useOpenConversation';
import {
  useEmbedSession,
  type EmbedComponentWait,
  type EmbedDataSource,
  type EmbedFilterContext,
} from '../../services/embedSessions.store';

const SOURCE_LABELS: Record<EmbedDataSource, { label: string; hint: string; icon: typeof Layers }> = {
  superset: {
    label: 'Superset',
    hint: 'Re-aggregated from a pre-built dashboard superset',
    icon: Layers,
  },
  node: {
    label: 'Table',
    hint: 'Read from an already-built table (no superset, no build)',
    icon: Database,
  },
  build: {
    label: 'Build',
    hint: 'A build had to run before this data could be read',
    icon: Hammer,
  },
};

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)} s`;
}

function fmtNum(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : new Intl.NumberFormat('en-US').format(n);
}

/** "Last 30 days", or the explicit window for a custom range. */
function dateRangeLabel(context: EmbedFilterContext | null): string {
  if (!context) return '—';
  const range = context.date_range;
  if (!range) return 'No date range';
  const pretty = range
    .toLowerCase()
    .split('_')
    .join(' ')
    .replace(/^./, (c) => c.toUpperCase());
  if (context.date_range_start && context.date_range_end) {
    return `${pretty} (${context.date_range_start} → ${context.date_range_end})`;
  }
  return pretty;
}

function FilterCell({
  context,
  dimensionNames,
}: {
  context: EmbedFilterContext | null;
  dimensionNames: Record<string, string>;
}) {
  if (!context) return <span className="text-muted-foreground">—</span>;

  const { dimension_filters: filters, metric_filter_count, chart_filter_count } = context;
  const extras: string[] = [];
  if (metric_filter_count) extras.push(`${metric_filter_count} metric filter${metric_filter_count === 1 ? '' : 's'}`);
  if (chart_filter_count) extras.push(`${chart_filter_count} chart filter${chart_filter_count === 1 ? '' : 's'}`);
  if (context.has_search_query) extras.push('search');

  if (filters.length === 0 && extras.length === 0) {
    return <span className="text-muted-foreground text-xs">No filters</span>;
  }

  return (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex flex-wrap gap-1">
        {filters.map((filter, idx) => {
          const name = (filter.dimension_id && dimensionNames[filter.dimension_id]) || filter.dimension_id || 'filter';
          const values = filter.values.join(', ');
          return (
            <Badge
              key={`${filter.dimension_id}-${idx}`}
              variant={filter.is_user_filter ? 'outline' : 'secondary'}
              className="text-[10px] font-normal max-w-[240px] truncate"
              title={`${name} ${filter.comparator ?? ''} ${values}${filter.is_user_filter ? ' (session scope)' : ''}`}
            >
              {name}
              {values ? `: ${values}` : ''}
            </Badge>
          );
        })}
      </div>
      {extras.length > 0 ? (
        <span className="text-[10px] text-muted-foreground">{extras.join(' · ')}</span>
      ) : null}
      {context.truncated ? (
        <span className="text-[10px] text-muted-foreground italic">values truncated</span>
      ) : null}
    </div>
  );
}

function SourceBadge({ wait }: { wait: EmbedComponentWait }) {
  const meta = SOURCE_LABELS[wait.source] ?? SOURCE_LABELS.node;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5" title={meta.hint}>
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      {meta.label}
    </span>
  );
}

export default function EmbedSessionDetail() {
  const { jti } = useParams<{ jti: string }>();
  const { data, isLoading, isError } = useEmbedSession(jti);
  const { open, pending, confirm, cancel, switching } = useOpenConversation();
  // Opens over this page rather than navigating, so the session stays in view.
  const [detailThreadId, setDetailThreadId] = useState<string | null>(null);

  const session = data?.record;
  const waits = data?.component_waits ?? [];
  const summary = data?.wait_summary;

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between w-full px-4 md:px-6 py-3 md:py-4 page-header-surface flex-shrink-0 z-10 gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <SidebarTrigger className="md:hidden h-5 w-5" />
          <div className="flex flex-col gap-1 md:gap-2 min-w-0">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/usage-analytics">Usage Analytics</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/usage-analytics/embed-sessions">Embed Sessions</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-mono">{jti}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <h1 className="text-lg md:text-xl font-semibold text-foreground font-mono truncate" title={jti}>
              {jti}
            </h1>
          </div>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/usage-analytics/embed-sessions">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading session…</p>
            </div>
          </div>
        ) : isError || !session ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">Failed to load this embed session.</p>
          </div>
        ) : (
          <div className="max-w-5xl space-y-6">
            {/* Session facts */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Session</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Fact label="Flow" value={session.flow} className="capitalize" />
                <Fact label="Embedded" value={session.target_label || session.target_id || '—'} />
                <Fact label="Sub" value={session.sub_id} mono />
                <Fact label="Email" value={session.email || '—'} />
                <Fact label="Branch" value={session.branch} />
                <Fact label="Role" value={session.role} />
                <Fact label="Created" value={fmtDateTime(session.created_at)} />
                <Fact
                  label="Opened"
                  value={session.redeemed_at ? fmtDateTime(session.redeemed_at) : 'Never opened'}
                />
                <Fact label="Expires" value={fmtDateTime(session.expires_at)} />
                <Fact label="TTL" value={`${session.ttl_seconds}s`} />
                <Fact label="Minted by" value={session.created_by_api_key_actor} mono />
                <Fact label="JTI" value={session.jti} mono />
              </CardContent>
            </Card>

            {/* Data scope */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Data scope</CardTitle>
                <CardDescription>
                  Row-level filters and metric restrictions this session was granted at mint time.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {(session.row_restrictions?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Row restrictions</p>
                    <div className="space-y-2">
                      {session.row_restrictions.map((r, idx) => (
                        <div key={idx} className="flex flex-wrap items-baseline gap-2 text-sm">
                          <span className="font-medium">{session.dimension_names?.[r.dimension] || r.dimension}</span>
                          <span className="text-muted-foreground">{r.comparator}</span>
                          {(Array.isArray(r.value) ? r.value : r.value != null ? [r.value] : []).map((value) => (
                            <Badge key={value} variant="secondary" className="font-mono text-xs">
                              {value}
                            </Badge>
                          ))}
                          <span className="text-xs text-muted-foreground">
                            {r.applies_to_metrics?.length
                              ? `for ${r.applies_to_metrics.join(', ')}`
                              : `for all metrics; unconnected metrics ${r.unreachable === 'deny' ? 'hidden' : 'shown'}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Dimension filters</p>
                  {Object.keys(session.data_scope || {}).length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">
                      {(session.row_restrictions?.length ?? 0) > 0
                        ? 'None.'
                        : 'No dimension filters — the session could see all rows.'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(session.data_scope).map(([key, values]) => (
                        <div key={key} className="flex flex-wrap items-baseline gap-2">
                          <span className="text-sm font-medium">
                            {session.dimension_names?.[key] || key}
                          </span>
                          <span className="text-muted-foreground text-sm">=</span>
                          {values.length === 0 ? (
                            <span className="text-sm text-muted-foreground italic">(no values)</span>
                          ) : (
                            values.map((value) => (
                              <Badge key={value} variant="secondary" className="font-mono text-xs">
                                {value}
                              </Badge>
                            ))
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-2">Blacklisted metrics</p>
                  {session.blacklisted_metric_ids.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">None — every metric was visible.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {session.blacklisted_metric_ids.map((id) => (
                        <Badge key={id} variant="outline" className="gap-1.5">
                          <Ban className="h-3 w-3" />
                          {session.blacklisted_metric_names?.[id] || id}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Time to data */}
            {(session.flow !== 'chat' || waits.length > 0) && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">Time to data</CardTitle>
                      <CardDescription>
                        {session.flow === 'chat'
                          ? "What each of Ellie's analyses waited on, measured server-side, and the window it ran for."
                          : 'What each component waited on, measured server-side, and the window it was looking at.'}
                      </CardDescription>
                    </div>
                    {summary && summary.component_count > 0 ? (
                      <div className="flex gap-6">
                        <div>
                          <p className="text-xs text-muted-foreground">Slowest component</p>
                          <p className="text-lg font-semibold tabular-nums">
                            {fmtMs(summary.slowest_wait_ms)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Components</p>
                          <p className="text-lg font-semibold tabular-nums">{summary.component_count}</p>
                        </div>
                        <div>
                          <p
                            className="text-xs text-muted-foreground"
                            title="Components whose own dataset had to be rebuilt"
                          >
                            Datasets built
                          </p>
                          <p className="text-lg font-semibold tabular-nums">{summary.build_count}</p>
                        </div>
                        {session.flow === 'dashboard' && summary.shared_build_ms !== null ? (
                          <div>
                            <p
                              className="text-xs text-muted-foreground"
                              title="A dashboard refresh reloads nothing until the whole build finishes, so every component waited this out"
                            >
                              Dashboard build
                            </p>
                            <p className="text-lg font-semibold tabular-nums">
                              {fmtMs(summary.shared_build_ms)}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent>
                  {waits.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No data loads recorded for this session. Sessions that were never opened —
                      or that predate wait tracking — show nothing here.
                    </p>
                  ) : (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Component</TableHead>
                            <TableHead>Date range</TableHead>
                            <TableHead>Filters</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead className="text-right">Build</TableHead>
                            <TableHead className="text-right">Query</TableHead>
                            <TableHead className="text-right">Total wait</TableHead>
                            <TableHead className="text-right">Rows</TableHead>
                            <TableHead>Loaded</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {waits.map((wait) => (
                            <TableRow key={wait.id}>
                              <TableCell>
                                <div className="flex flex-col min-w-0">
                                  <span className="font-medium truncate">
                                    {wait.analysis_name || wait.analysis_id || 'Unknown chart'}
                                  </span>
                                  {wait.origin === 'chat_tool' ? (
                                    <span
                                      className="text-[10px] text-muted-foreground"
                                      title="Ellie's generate_and_run_analysis tool — the user waited on this inside the chat turn"
                                    >
                                      Ellie analysis
                                    </span>
                                  ) : null}
                                  {wait.error ? (
                                    <span className="flex items-center gap-1 text-xs text-destructive">
                                      <AlertTriangle className="h-3 w-3" />
                                      {wait.error}
                                    </span>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                {dateRangeLabel(wait.filter_context)}
                                {wait.filter_context?.previous_period ? (
                                  <span className="block text-[10px]">vs previous period</span>
                                ) : null}
                                {wait.filter_context?.comparison_type ? (
                                  <span className="block text-[10px]">
                                    vs {wait.filter_context.comparison_type.toLowerCase().split('_').join(' ')}
                                  </span>
                                ) : null}
                              </TableCell>
                              <TableCell>
                                <FilterCell
                                  context={wait.filter_context}
                                  dimensionNames={session.dimension_names || {}}
                                />
                              </TableCell>
                              <TableCell><SourceBadge wait={wait} /></TableCell>
                              <TableCell className="text-right tabular-nums">{fmtMs(wait.build_ms)}</TableCell>
                              <TableCell className="text-right tabular-nums">{fmtMs(wait.query_ms)}</TableCell>
                              <TableCell className="text-right tabular-nums font-medium">
                                {fmtMs(wait.total_wait_ms)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums text-muted-foreground">
                                {fmtNum(wait.row_count)}
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-muted-foreground text-xs">
                                {fmtDateTime(wait.created_at)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {session.flow === 'dashboard' && summary?.shared_build_ms != null ? (
                    <p className="text-xs text-muted-foreground mt-3">
                      The <span className="font-medium">Build</span> column is the time spent
                      rebuilding that component's own dataset. Components served from an
                      already-built superset or table show no build time, but still waited{' '}
                      {fmtMs(summary.shared_build_ms)} for the dashboard refresh to finish before
                      their data reloaded.
                    </p>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Chat usage */}
            {session.flow === 'chat' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Conversations</h2>
                  <p className="text-sm text-muted-foreground">
                    LLM usage for the conversations this session drove.
                  </p>
                </div>
                {(data?.thread_ids ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No conversations recorded for this session.</p>
                ) : (
                  data!.thread_ids.map((threadId) => (
                    <ThreadUsageCard
                      key={threadId}
                      threadId={threadId}
                      onOpenConversation={open}
                      onOpenUsageDetail={setDetailThreadId}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ConversationModeSwitchDialog
        pending={pending}
        onConfirm={confirm}
        onCancel={cancel}
        switching={switching}
      />

      <ThreadUsageModal threadId={detailThreadId} onClose={() => setDetailThreadId(null)} />
    </div>
  );
}

function Fact({
  label,
  value,
  mono = false,
  className = '',
}: {
  label: string;
  value: string;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`text-sm text-foreground truncate ${mono ? 'font-mono text-xs' : ''} ${className}`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}
